import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  Outlet,
  generatePath,
  matchRoutes,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { UnitDetailTopbar } from 'components/layout/UnitDetailTopbar';
import { HmiMenuBackButton } from 'components/layout/UnitDetailTopbar/HmiMenuBackButton';
import { CHAT_DEFAULT_WIDTH } from 'features/assetControl/assetControl.constants';
import { OperatorChat } from 'features/assetControl/components/OperatorChat';
import { useGetAsset } from 'features/assetControl/hooks/useGetAsset';
import { use404RedirectIf } from 'hooks/use404RedirectIf';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { getDefaultRangeInHours, useTimerange } from 'hooks/useTimerange';
import { useUnitDetailTopbarState } from 'hooks/useUnitDetailTopbarState';
import useUnitsConnectionStatus from 'hooks/useUnitsConnectionStatus';
import { sortBy } from 'lodash';
import { routes } from 'routes';
import { GreenerUnit } from 'types';
import { getAssetLabel } from 'utils/formatters';
import { isBatteryAsset, isReportingOnly } from 'utils/units';
import { useAssetSidebar } from './[Asset].sidebar';

export const AssetLayout = () => {
  useAssetSidebar();
  const { unitExternalIdentifier = '' } = useParams();
  const { units } = useOutletContext<{ units: GreenerUnit[] }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation();

  const { asset } = useGetAsset(unitExternalIdentifier);

  const {
    postFix,
    pageTitleKey,
    isHmiTabPage,
    isChatVisible,
    isChatSplit,
    setIsChatSplit,
    isOpen,
    setIsOpen,
    rightTopbarSlot,
    setRightTopbarSlot,
  } = useUnitDetailTopbarState({ pathname: location.pathname, asset });

  const pageTitle = pageTitleKey ? t(pageTitleKey) : undefined;

  const leftTopbarSlot =
    isMobile && isHmiTabPage ? (
      <HmiMenuBackButton onClick={() => navigate(`/assets/${unitExternalIdentifier}/hmi/menu`)} />
    ) : null;

  //@ts-expect-error check react router 6 for type mismatch
  const matchedRoutes = matchRoutes(routes, location);

  use404RedirectIf(!asset?.externalIdentifier);

  const isOnline = useUnitsConnectionStatus()?.[asset?.externalIdentifier] ?? false;

  const { range, ...timerangeProps } = useTimerange(undefined, getDefaultRangeInHours());

  if (!asset?.externalIdentifier) {
    return null;
  }

  const isReportingOnlyAsset = isReportingOnly(asset);
  const isReportsPage = location.pathname.includes('reports');

  if (isReportsPage && !isReportingOnlyAsset) {
    return <Navigate to="/" replace />;
  }

  if (!isReportsPage && isReportingOnlyAsset) {
    return (
      <Navigate to={generatePath('/assets/:unitExternalIdentifier/reports', { unitExternalIdentifier })} replace />
    );
  }

  function onChangeUnit(newSelectedUnit: GreenerUnit) {
    const matched = matchedRoutes?.find((m) => m.pathnameBase === location.pathname);

    if (!matched) {
      return;
    }

    const isHmiRoute = matched.pathnameBase.includes('/hmi/');
    const isNewAssetReportingOnly = isReportingOnly(newSelectedUnit);

    if (isHmiRoute && isBatteryAsset(newSelectedUnit)) {
      navigate(
        generatePath('/assets/:unitExternalIdentifier/hmi/:tab', {
          unitExternalIdentifier: newSelectedUnit.externalIdentifier,
          tab: postFix,
        })
      );
      return;
    }

    let newPath = isNewAssetReportingOnly
      ? '/assets/:unitExternalIdentifier/reports'
      : '/assets/:unitExternalIdentifier';

    if (!isHmiRoute && matched.pathnameBase.includes('/graphs') && !isNewAssetReportingOnly) {
      newPath = '/assets/:unitExternalIdentifier/graphs';
    }

    navigate(
      generatePath(newPath, {
        unitExternalIdentifier: newSelectedUnit.externalIdentifier,
      })
    );
  }

  return (
    <>
      <Helmet key={location.pathname}>
        <title>{unitExternalIdentifier}</title>
      </Helmet>
      <Box
        sx={{
          transition: 'margin-right 0.3s',
          marginRight: !isMobile && isChatSplit ? `${CHAT_DEFAULT_WIDTH}px` : 0,
        }}
      >
        <UnitDetailTopbar
          title={pageTitle}
          rightSlot={rightTopbarSlot}
          leftSlot={leftTopbarSlot}
          isSticky={isMobile && location.pathname.includes('/hmi')}
          unitSelectorProps={{
            units: sortBy(units, ['externalIdentifier']),
            onChange: onChangeUnit,
            selectedUnitExtId: unitExternalIdentifier,
          }}
          showRangePicker={location.pathname.includes('graphs')}
          timerangePickerProps={{
            range,
            ...timerangeProps,
          }}
        />
        <Outlet
          key={unitExternalIdentifier}
          context={{
            range,
            selectedAsset: asset,
            assetLabels: [getAssetLabel(asset)],
            isOnline,
            isChatSplit,
            setRightTopbarSlot,
          }}
        />
      </Box>

      {isChatVisible && (
        <OperatorChat
          unit={asset}
          isOnline={isOnline}
          isChatSplit={isChatSplit}
          setIsChatSplit={setIsChatSplit}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      )}
    </>
  );
};
